import { navigationLinks, type NavigationLink } from './navigationLinks';
import { useAuth, useAuthRoles } from '@presov.sk/presov-auth-react';
import type { NavItem } from '@presov.sk/presov-ui-react';

/**
 * `navigationLinks` (dáta aplikácie) → `NavItem[]` pre shell z knižnice.
 *
 * Oprávnenia sa vyhodnocujú **tu**, v aplikácii - shell nikdy nečíta token, len
 * dostane `isVisible` na každej položke. Neprihlásený vidí len `isPublic`
 * položky, presne ako doteraz.
 *
 * Čo tým padá: `hasSomePermission` na sekcii „Administrácia" bola ručne
 * vypísaná únia oprávnení jej detí (vrátane cudzieho `client_id: 'cimpo'`), aby
 * sa prázdna sekcia nekreslila. Shell si to odvodí sám - sekciu bez viditeľných
 * detí nenakreslí - takže tá únia sa už nemusí pri každej zmene dieťaťa
 * prepisovať. `iconActive` zaniká: aktívny stav nesie farba ikony.
 */
export const useNavItems = (): NavItem[] => {
  const { authUser } = useAuth();
  const { hasSomePermission, hasEveryPermission } = useAuthRoles();

  const isVisible = (link: NavigationLink) => {
    if (!authUser) {
      return link.isPublic === true;
    }

    if (
      link.hasSomePermission?.length &&
      !hasSomePermission(link.hasSomePermission)
    ) {
      return false;
    }

    if (
      link.hasEveryPermission?.length &&
      !hasEveryPermission(link.hasEveryPermission)
    ) {
      return false;
    }

    return true;
  };

  const toNavItem = (link: NavigationLink): NavItem => ({
    href: link.href,
    label: link.label,
    mobileLabel: link.mobileLabel,
    icon: link.icon,
    tone: link.tone,
    isVisible: isVisible(link),
    children: link.children?.map(toNavItem),
  });

  return navigationLinks.map(toNavItem);
};
